import { Button, Modal, Input } from 'antd';
import { DownloadOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { NotificationAlert, Table } from '../../../components';
import { assignedMenteescolumns } from '../../mentor/assigned-mentees/columns';
import { exportToExcel } from './helper';
import { useEffect, useRef, useState } from 'react';
import QuestionIcon from '../../../assets/question-circle.svg';
import { useLocation } from 'react-router-dom';
import {
  getQuarter,
  getQuarterlyUsers,
  updateQuarter,
} from '../../../services/reviewsServices';
import dayjs from 'dayjs';
import {
  objectiveStatuses,
  quarterReviewStatuses,
  statusColors,
  statusesLabel,
} from '../../../helper';
import {
  AllReviewsProps,
  quarterlyUsers,
  updateQuarterProps,
} from '../../../types';
import { SearchProps } from 'antd/es/input';

const { Search } = Input;
let searchtimer: ReturnType<typeof setTimeout>;

const QuarterEmployees = () => {
  const [modal, contextHolder] = Modal.useModal();
  const [exportLoading, setExportLoading] = useState(false);
  const [reviewData, setReviewData] = useState({
    'Start Date': '-',
    'End Date': '-',
    'Mentee Deadline': '-',
    'Mentor Deadline': '-',
    Name: '',
  });
  const myRef = useRef<any>();
  const [allReviewData, setAllReviewData] = useState<AllReviewsProps>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<quarterlyUsers[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [filtered, setFiltered] = useState<quarterlyUsers[]>([]);
  const { id } = useLocation().state;

  // fetch quarter details
  const fetchReviewData = async () => {
    const { data, error } = await getQuarter(id || '', 'hr');
    if (error) return myRef.current?.call('error', error?.message);
    setAllReviewData(data);
    const status = quarterReviewStatuses[data?.status || 0];
    setReviewData({
      'Start Date': dayjs(data?.startDate).format('MMM DD YYYY'),
      'End Date': dayjs(data?.endDate).format('MMM DD YYYY'),
      'Mentee Deadline': dayjs(data?.menteeDeadline).format('MMM DD YYYY'),
      'Mentor Deadline': dayjs(data?.mentorDeadline).format('MMM DD YYYY'),
      [status]: data?.progress,
      Name: data?.name || '',
    });
  };

  // fetch quarter users
  const fetchQuarterusers = async () => {
    setFetchLoading(true);
    const { data, error } = await getQuarterlyUsers(id || '', 'hr');
    setFetchLoading(false);
    if (error) return myRef.current?.call('error', error?.message);
    if (data) {
      setData(data);
      setFiltered(data);
    }
  };

  useEffect(() => {
    fetchReviewData();
    fetchQuarterusers();
  }, []);

  // update quarter review
  const updateQuarterReview = async (status: number) => {
    setLoading(true);
    const { error } = await updateQuarter({
      ...(allReviewData as updateQuarterProps),
      status,
    });
    setLoading(false);
    if (error) return myRef.current?.call('error', error?.message);
    fetchReviewData();
    return true;
  };

  const handleInit = () => {
    modal.confirm({
      title: 'Are you sure you want to initiate this review cycle?',
      icon: <img src={QuestionIcon} alt="" width={28} className="mr-3" />,
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk() {
        return updateQuarterReview(1);
      },
    });
  };

  const handleClose = () => {
    modal.confirm({
      title: 'Are you sure you want to close this review cycle?',
      icon: <ExclamationCircleFilled />,
      okText: 'Yes',
      cancelText: 'Cancel',
      onOk() {
        return updateQuarterReview(3);
      },
    });
  };

  const handleExportData = () => {
    setExportLoading(true);
    exportToExcel(
      data?.map((item) => {
        return {
          name: item.user.name,
          designation: item.user.designation,
          tier: item.user.tier.toString(),
          noOfObjectives: item.user.objectiveCount.toString(),
          mentorStatus: item.mentorStatus,
          menteeStatus: item.menteeStatus,
          mentorName: item.user.mentor?.name || '-',
        };
      }),
      {
        startDate: reviewData['Start Date'],
        endDate: reviewData['End Date'],
        menteeDeadline: reviewData['Mentee Deadline'],
        mentorDeadline: reviewData['Mentor Deadline'],
        progress: allReviewData ? allReviewData.progress.toString() : '0',
        progressStatus: allReviewData
          ? objectiveStatuses[allReviewData.status].label
          : '',
      },
      `${reviewData.Name} - ${dayjs().format('MMM DD YY')}`
    );
    setExportLoading(false);
  };

  const filterData = (value: string) =>
    data?.filter(
      (item) =>
        item?.user.name?.toLocaleLowerCase().includes(value) ||
        item?.user.designation?.toLocaleLowerCase().includes(value) ||
        item?.user.tier?.toString()?.toLocaleLowerCase().includes(value)
    );

  const onSearch: SearchProps['onSearch'] = (value) => {
    setLoading(true);
    value = value?.toLocaleLowerCase();
    const filter = filterData(value);
    setFiltered(filter);
    setLoading(false);
  };

  const handleChange = (value: string) => {
    if (value !== '') {
      clearTimeout(searchtimer);
      searchtimer = setTimeout(() => {
        value = value?.toLocaleLowerCase();
        const filter = filterData(value);
        setFiltered(filter);
      }, 600);
    } else {
      clearTimeout(searchtimer);
      setFiltered(data);
    }
  };

  return (
    <div className="flex flex-col gap-y-[10px]">
      <div className="p-[10px] mb-[-10px]">
        <div className="text-[20px] font-[500]">{reviewData?.Name}</div>
        <div className="opacity-45">
          Initiate/close a quarterly feedback cycle and export a full report of
          this cycle to excel.
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div>
          <Search
            placeholder="Search Name / Designation / Tier"
            onSearch={onSearch}
            className="w-[280px] ml-auto"
            onChange={(e) => handleChange(e.target.value)}
          />
        </div>
        <div className="flex gap-x-[10px] p-[10px]">
          {Object.entries(reviewData).map((value, idx: number) => {
            if (value[0] === 'Name') return null;
            if (statusesLabel[value[0]]) {
              return (
                <div
                  key={idx}
                  style={{ background: `${statusColors[value[0]] || ''}` }}
                  className="flex flex-col rounded-[5px] text-white px-[10px] py-1"
                >
                  <div>{statusesLabel[value[0]]}</div>
                  <div className="text-base font-[500]">{`${value[1]}% Complete`}</div>
                </div>
              );
            }
            return (
              <div key={idx} className="flex flex-col px-[10px] py-1">
                <div className="opacity-50">{value[0]}</div>
                <div className="opacity-85">{value[1]}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex mb-4">
        <div className="flex gap-x-[10px]">
          <Button
            type="primary"
            onClick={handleInit}
            loading={loading}
            disabled={loading || allReviewData?.status !== 0}
          >
            Initiate Cycle
          </Button>
          <Button
            onClick={handleClose}
            loading={loading}
            disabled={
              loading ||
              allReviewData?.status === 0 ||
              allReviewData?.status === 3
            }
          >
            Close Cycle
          </Button>
        </div>
        <div className="ml-auto">
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportData}
            loading={exportLoading}
            disabled={reviewData.Name === ''}
          >
            Export to Excel
          </Button>
        </div>
      </div>

      <Table
        columns={assignedMenteescolumns(false, reviewData.Name, id)}
        data={filtered}
        errorFetchingData={false}
        loading={fetchLoading}
        rowKey={(data) => data.user.id}
      />
      {contextHolder}
      <NotificationAlert ref={myRef} />
    </div>
  );
};

export default QuarterEmployees;
